import { Box, Group, Stack, Text } from '@mantine/core';
import { IconCheck, IconExclamationMark } from '@tabler/icons-react';
import { z } from 'zod';

interface PasswordRequirement {
  label: string;
  regex: RegExp;
}

const PASSWORD_REQUIREMENTS: readonly PasswordRequirement[] = [
  { label: '8+ characters', regex: /.{8,}/ },
  { label: 'One uppercase letter (A-Z)', regex: /[A-Z]/ },
  { label: 'One number (0-9)', regex: /[0-9]/ },
  { label: 'One special character (e.g. ! @ # $ % & * ?)', regex: /[!@#$%&*?]/ },
] as const;

export const passwordSchema = z.object({
  password: PASSWORD_REQUIREMENTS.reduce(
    (schema, req) => schema.regex(req.regex, req.label),
    z.string(),
  ),
});

export type PasswordFormValues = z.infer<typeof passwordSchema>;

const testPasswordRequirement = (password: string, regex: RegExp): boolean => regex.test(password);

export const allPasswordRequirementsMet = (password: string): boolean =>
  PASSWORD_REQUIREMENTS.every(req => testPasswordRequirement(password, req.regex));

interface PasswordRequirementsProps {
  password: string;
}

export const PasswordRequirements = ({ password }: PasswordRequirementsProps) => (
  <Stack gap="xs">
    {PASSWORD_REQUIREMENTS.map(req => {
      const met = testPasswordRequirement(password, req.regex);
      return (
        <Group key={req.label} gap="xs" justify="space-between" pr="xs">
          <Text size="xs" c={met ? 'green' : 'gray.6'}>
            {req.label}
          </Text>
          {met ? (
            <IconCheck size={16} color="var(--mantine-color-green-6)" />
          ) : (
            <Box
              style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                border: '1.5px solid var(--mantine-color-gray-5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconExclamationMark size={10} color="var(--mantine-color-gray-5)" stroke={2.5} />
            </Box>
          )}
        </Group>
      );
    })}
  </Stack>
);
